import React from 'react';
import { Link } from 'react-router-dom';
import { Alert, AlertDescription } from './ui/alert';
import { useTenant } from '../contexts/TenantContext';
import { adjustColor, resolveImageUrl } from '../utils/colorUtils';
import { getTenantTitleSx } from '../utils/titleStyle';

const LoginLayout = ({ title, subtitle, children, footerLink }) => {
  const { theme: tenantTheme, tenantName, isValid, error: tenantError } = useTenant();

  const primaryColor = tenantTheme?.primaryColor || '#2d3436';
  const logoUrl = resolveImageUrl(tenantTheme?.logoUrl);
  const titleStyle = getTenantTitleSx(tenantTheme?.titleStyle, { baseFontSize: 1.75 });
  const companyName = tenantTheme?.companyName || tenantName || 'Thaklis';

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-4"
      style={{
        background: `linear-gradient(160deg, ${adjustColor(primaryColor, 15)} 0%, ${primaryColor} 40%, ${adjustColor(primaryColor, -30)} 100%)`,
      }}
    >
      <div className="w-full max-w-sm flex flex-col items-center">
        {/* Tenant Error Banner */}
        {!isValid && tenantError && (
          <Alert className="mb-4 bg-amber-50 border-amber-200 text-amber-800 w-full">
            <AlertDescription>{tenantError}</AlertDescription>
          </Alert>
        )}

        {/* Logo */}
        <div className="mb-4">
          <img
            src={logoUrl || '/thaklis-logo.png'}
            alt={companyName}
            className="h-24 mx-auto object-contain"
            style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))' }}
          />
        </div>

        {/* Company Name */}
        <h1
          className="text-white text-center mb-1"
          style={{
            ...titleStyle,
            textShadow: '0 1px 3px rgba(0,0,0,0.1)',
          }}
        >
          {companyName}
        </h1>

        {/* Subtitle label */}
        {title && (
          <p
            className="text-center mb-6"
            style={{
              fontSize: '0.65rem',
              fontWeight: 500,
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.6)',
            }}
          >
            {title}
          </p>
        )}
        {subtitle && (
          <p className="text-xs text-center mb-6" style={{ color: 'rgba(255,255,255,0.55)' }}>
            {subtitle}
          </p>
        )}

        {/* Login Card (children) */}
        {children}

        {/* Cross-link */}
        {footerLink && (
          <p className="text-center text-xs mt-4" style={{ color: 'rgba(255,255,255,0.6)' }}>
            <Link
              to={footerLink.href}
              className="hover:underline transition-colors"
              style={{ color: 'rgba(255,255,255,0.7)' }}
            >
              {footerLink.text}
            </Link>
          </p>
        )}

        {/* Copyright */}
        <p className="text-center text-xs mt-4" style={{ color: 'rgba(255,255,255,0.5)' }}>
          &copy; {new Date().getFullYear()} {companyName}. All rights reserved.
        </p>

        {/* Powered by Thaklis */}
        <div className="flex items-center justify-center gap-1.5 mt-2 mb-4 opacity-50 hover:opacity-70 transition-opacity">
          <img
            src="/thaklis-logo.png"
            alt="Thaklis"
            className="h-4 object-contain"
          />
          <span className="text-white text-[10px] tracking-wide">Powered by Thaklis</span>
        </div>
      </div>
    </div>
  );
};

export default LoginLayout;
